import React from "react";
import { Typography } from "antd";
import { Trans } from "react-i18next";
import { useSelector } from "react-redux";

import { selectCurrentApplication } from "../../../../../store/selector";

const { Paragraph } = Typography;

const transComponents = Object.freeze({ bold: <strong />, br: <br /> });

/**
 * Native JSON field type `consentText`: the legal/consent copy the EE forms render as PLAIN text — a
 * single `<Paragraph>`, exactly the way the hand-written forms did. It is NOT the boxed blue info Note
 * (that is the `infoNotice` field type's job); routing this copy through Note added an unwanted info box
 * around the free-drug submit-attestation text (MVP-31223 QA). It reads the current application's drug
 * name INTERNALLY and registers NOTHING (display-only, bundle unchanged).
 *
 * Two authoring shapes:
 *  - `segments`: an array of `{ i18nKey, excludeDrugs?, includeDrugs? }` rendered as ONE paragraph (the
 *    copy reads as continuous text; the keys carry their own <br> breaks, so a paragraph per segment
 *    would add blank-line gaps, and a gated-out segment would leave an empty row behind).
 *  - a single `{ i18nKey, excludeDrugs?, includeDrugs? }` (one segment declared inline).
 *
 * Each segment may be drug-gated (use at most one filter): `excludeDrugs` hides the copy for the listed
 * drugs (the default "show unless…" case); `includeDrugs` shows it ONLY for the listed drugs and stays
 * hidden while no drug is known (the "drug-specific" case, e.g. free-drug's Evrysdi-only consent).
 *
 * FormJson usage:
 *   { "type": "consentText", "name": "", "colSize": 24,
 *     "dedicatedFieldProps": { "segments": [
 *       { "i18nKey": "<always-shown key>" },
 *       { "i18nKey": "<gated key>", "excludeDrugs": ["bosulif", "xalkori"] }
 *     ] } }
 *   { "type": "consentText", "name": "", "colSize": 24,
 *     "dedicatedFieldProps": { "i18nKey": "<key>", "includeDrugs": ["evrysdi"] } }
 */
export const ConsentTextField = React.memo(({ segments, i18nKey, excludeDrugs, includeDrugs }) => {
  const currentApplication = useSelector(selectCurrentApplication);
  const drug = currentApplication?.drugDisplayName?.toLowerCase();
  const normalize = (drugs) => drugs.map((d) => String(d).toLowerCase());

  const isVisibleForDrug = ({ excludeDrugs, includeDrugs } = {}) => {
    if (Array.isArray(excludeDrugs) && drug && normalize(excludeDrugs).includes(drug)) {
      return false;
    }
    if (Array.isArray(includeDrugs) && !(drug && normalize(includeDrugs).includes(drug))) {
      return false;
    }
    return true;
  };

  // Accept the multi-segment API (segments: [...]) or a single segment declared inline; normalize to one
  // list so there is a single plain-paragraph render path for both.
  const allSegments =
    Array.isArray(segments) && segments.length ? segments : i18nKey ? [{ i18nKey, excludeDrugs, includeDrugs }] : [];
  const visibleSegments = allSegments.filter((segment) => segment.i18nKey && isVisibleForDrug(segment));

  if (!visibleSegments.length) {
    return null;
  }

  return (
    <Paragraph>
      {visibleSegments.map(({ i18nKey }) => (
        <Trans key={i18nKey} i18nKey={i18nKey} components={transComponents} />
      ))}
    </Paragraph>
  );
});

ConsentTextField.displayName = "ConsentTextField";

export default ConsentTextField;
